import React from 'react';

import Icon from '../../../components/AppIcon';

const CompletionChecklist = ({ data, completedSections }) => {
  const checklistItems = [
    {
      id: 'title',
      label: 'Problem title is clear and descriptive',
      section: 'overview',
      required: true,
      completed: !!data?.title && data?.title?.length >= 10
    },
    {
      id: 'description',
      label: 'Problem description is detailed (minimum 100 characters)',
      section: 'overview',
      required: true,
      completed: !!data?.description && data?.description?.length >= 100
    },
    {
      id: 'category',
      label: 'Problem category is selected',
      section: 'overview',
      required: true,
      completed: !!data?.category
    },
    {
      id: 'priority',
      label: 'Priority level is set',
      section: 'overview',
      required: true,
      completed: !!data?.priority
    },
    {
      id: 'objectives',
      label: 'At least 2 project objectives are defined',
      section: 'requirements',
      required: true,
      completed: data?.objectives && data?.objectives?.length >= 2
    },
    {
      id: 'deliverables',
      label: 'Expected deliverables are specified',
      section: 'requirements',
      required: true,
      completed: data?.expectedDeliverables && data?.expectedDeliverables?.length > 0
    },
    {
      id: 'primaryContact',
      label: 'Primary contact information is complete',
      section: 'stakeholders',
      required: true,
      completed: data?.primaryContact && data?.primaryContact?.name && data?.primaryContact?.email
    },
    {
      id: 'timeline',
      label: 'Project duration and dates are set',
      section: 'timeline',
      required: true,
      completed: !!data?.projectDuration && !!data?.projectStartDate
    },
    {
      id: 'background',
      label: 'Background context is provided',
      section: 'overview',
      required: false,
      completed: !!data?.background && data?.background?.length > 0
    },
    {
      id: 'constraints',
      label: 'Project constraints are identified',
      section: 'requirements',
      required: false,
      completed: data?.constraints && data?.constraints?.length > 0
    },
    {
      id: 'milestones',
      label: 'Key milestones are defined',
      section: 'timeline',
      required: false,
      completed: data?.milestones && data?.milestones?.length > 0
    },
    {
      id: 'attachments',
      label: 'Supporting documents are uploaded',
      section: 'files',
      required: false,
      completed: data?.attachments && data?.attachments?.length > 0
    }
  ];

  const requiredItems = checklistItems?.filter(item => item?.required);
  const optionalItems = checklistItems?.filter(item => !item?.required);
  
  const completedRequired = requiredItems?.filter(item => item?.completed)?.length;
  const completedOptional = optionalItems?.filter(item => item?.completed)?.length;
  
  const totalRequired = requiredItems?.length;
  const totalOptional = optionalItems?.length;
  
  const isReadyToSubmit = completedRequired === totalRequired;
  const completionPercentage = Math.round((completedRequired / totalRequired) * 100);

  const getSectionIcon = (section) => {
    const icons = {
      'overview': 'FileText',
      'requirements': 'Target',
      'stakeholders': 'Users',
      'timeline': 'Calendar',
      'files': 'Upload'
    };
    return icons?.[section] || 'CheckCircle';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="CheckSquare" size={16} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Completion Checklist</h3>
            <p className="text-sm text-muted-foreground">
              {completedRequired}/{totalRequired} required items completed
            </p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-bold text-foreground">{completionPercentage}%</div>
          <div className="text-xs text-muted-foreground">Complete</div>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm text-muted-foreground">{completedRequired}/{totalRequired}</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-layout ${
              isReadyToSubmit ? 'bg-success' : 'bg-primary'
            }`}
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>
      {/* Required Items */}
      <div className="space-y-4 mb-6">
        <h4 className="font-medium text-foreground flex items-center space-x-2">
          <Icon name="AlertCircle" size={16} className="text-error" />
          <span>Required Items</span>
        </h4>
        
        <div className="space-y-3">
          {requiredItems?.map((item) => (
            <div key={item?.id} className="flex items-start space-x-3">
              <div className="mt-0.5">
                {item?.completed ? (
                  <Icon name="CheckCircle" size={16} className="text-success" />
                ) : (
                  <Icon name="Circle" size={16} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name={getSectionIcon(item?.section)} size={14} className="text-muted-foreground" />
                  <span className={`text-sm ${
                    item?.completed ? 'text-foreground line-through' : 'text-foreground'
                  }`}>
                    {item?.label}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Optional Items */}
      <div className="space-y-4">
        <h4 className="font-medium text-foreground flex items-center space-x-2">
          <Icon name="Plus" size={16} className="text-accent" />
          <span>Optional Enhancements ({completedOptional}/{totalOptional})</span>
        </h4>
        
        <div className="space-y-3">
          {optionalItems?.map((item) => (
            <div key={item?.id} className="flex items-start space-x-3">
              <div className="mt-0.5">
                {item?.completed ? (
                  <Icon name="CheckCircle" size={16} className="text-success" />
                ) : (
                  <Icon name="Circle" size={16} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name={getSectionIcon(item?.section)} size={14} className="text-muted-foreground" />
                  <span className={`text-sm ${
                    item?.completed ? 'text-foreground line-through' : 'text-muted-foreground'
                  }`}>
                    {item?.label}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Submission Status */}
      <div className={`mt-6 p-4 rounded-lg border ${
        isReadyToSubmit 
          ? 'bg-success/10 border-success/20' :'bg-warning/10 border-warning/20'
      }`}>
        <div className="flex items-center space-x-3">
          <Icon 
            name={isReadyToSubmit ? "CheckCircle" : "AlertTriangle"} 
            size={20} 
            className={isReadyToSubmit ? "text-success" : "text-warning"} 
          />
          <div>
            <h4 className={`font-medium ${
              isReadyToSubmit ? 'text-success' : 'text-warning'
            }`}>
              {isReadyToSubmit ? 'Ready to Submit!' : 'Incomplete Submission'}
            </h4>
            <p className="text-sm text-muted-foreground">
              {isReadyToSubmit 
                ? 'All required information has been provided. You can now submit your problem statement.'
                : `Please complete ${totalRequired - completedRequired} more required item${totalRequired - completedRequired !== 1 ? 's' : ''} before submitting.`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionChecklist;